import { Injectable } from '@angular/core';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { catchError, first, map, mergeMap, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { ByIdPayload, ByKey, IdType, NormalizedList, NormalizedSingle } from '@industi/ngx-common';
import { select, Store } from '@ngrx/store';
import { SymfonyPaginationParams } from '../../symfony/list/models/symfony-pagination-params';
import { getInstanceLocale } from '../../instance/instance.reducer';
import { HttpErrorResponse } from '@angular/common/http';
import { of } from 'rxjs';
import { ResponseStatus } from '../../../shared/enums/response-status.enum';
import { LibSnackbarComponent } from '@industi/ngx-modules';
import { AppGlobal } from '../../../shared/app-global';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  DictionaryCourierEditAction,
  DictionaryCourierEditActionTypes, DictionaryCourierEditFailAction,
  DictionaryCourierEditSuccessAction
} from './actions/edit.actions';
import {
  DictionaryCourierDeleteAction, DictionaryCourierDeleteActionTypes,
  DictionaryCourierDeleteFailAction,
  DictionaryCourierDeleteSuccessAction
} from './actions/delete.actions';
import {
  DictionaryCourierLoadListAction,
  DictionaryCourierLoadListActionTypes,
  DictionaryCourierLoadListClearIdsAction, DictionaryCourierLoadListFailAction, DictionaryCourierLoadListSuccessAction
} from './actions/list.actions';
import {
  DictionaryCourierLoadDetailsAction, DictionaryCourierLoadDetailsActionTypes, DictionaryCourierLoadDetailsFailAction,
  DictionaryCourierLoadDetailsSuccessAction
} from './actions/load-details.actions';
import {
  DictionaryCourierAddAction,
  DictionaryCourierAddActionTypes, DictionaryCourierAddFailAction,
  DictionaryCourierAddSuccessAction
} from './actions/add.actions';
import { createDictionaryCourierListParams } from './selectors/get-load-list.selectors';
import { DictionaryCourierService } from './courier.service';
import { AlertUtil } from '../../../shared/utils/alert.util';

@Injectable()
export class DictionaryCourierEffects {

  @Effect()
  loadList$ = this.actions$.pipe(
    ofType(DictionaryCourierLoadListActionTypes.request),
    map((action: DictionaryCourierLoadListAction) => action.payload),
    switchMap((payload: ByIdPayload) => this.store.pipe(
      select(createDictionaryCourierListParams(payload.id)),
      map((params: SymfonyPaginationParams) => [payload.id, params] as [IdType, SymfonyPaginationParams]),
      first()
    )),
    withLatestFrom(this.store.pipe(select(getInstanceLocale))),
    switchMap(([[ id, params ], locale]) => this.service.loadList(locale, params).pipe(
      mergeMap((res: NormalizedList) => [
        new DictionaryCourierLoadListClearIdsAction({ id }),
        new DictionaryCourierLoadListSuccessAction({ id, res })]),
      catchError((errors: HttpErrorResponse) => of(new DictionaryCourierLoadListFailAction({ id, errors })))
    ))
  );

  @Effect()
  add$ = this.actions$.pipe(
    ofType(DictionaryCourierAddActionTypes.request),
    map((action: DictionaryCourierAddAction) => action.payload),
    withLatestFrom(this.store.pipe(select(getInstanceLocale))),
    switchMap(([{ id, data }, locale]) => this.service.add(locale, data).pipe(
      map((res: NormalizedSingle) => new DictionaryCourierAddSuccessAction({ res, id })),
      catchError((errors: HttpErrorResponse) => of(new DictionaryCourierAddFailAction({ errors, id })))
    ))
  );

  @Effect()
  edit$ = this.actions$.pipe(
    ofType(DictionaryCourierEditActionTypes.request),
    map((action: DictionaryCourierEditAction) => action.payload),
    withLatestFrom(this.store.pipe(select(getInstanceLocale))),
    switchMap(([{ id, data }, locale]) => this.service.edit(locale, data, id).pipe(
      map((res: NormalizedSingle) => new DictionaryCourierEditSuccessAction({ res, id })),
      catchError((errors: HttpErrorResponse) => of(new DictionaryCourierEditFailAction({ errors, id })))
    ))
  );

  @Effect()
  loadDetails$ = this.actions$.pipe(
    ofType(DictionaryCourierLoadDetailsActionTypes.request),
    map((action: DictionaryCourierLoadDetailsAction) => action.payload.id),
    withLatestFrom(this.store.pipe(select(getInstanceLocale))),
    switchMap(([id, locale]) => this.service.details(locale, id).pipe(
      map((res: NormalizedSingle) => new DictionaryCourierLoadDetailsSuccessAction({ id, res })),
      catchError((errors: HttpErrorResponse) => of(new DictionaryCourierLoadDetailsFailAction({ id, errors })))
    ))
  );

  @Effect()
  delete$ = this.actions$.pipe(
    ofType(DictionaryCourierDeleteActionTypes.request),
    map((action: DictionaryCourierDeleteAction) => action.payload.id),
    withLatestFrom(this.store.pipe(select(getInstanceLocale))),
    switchMap(([id, locale]) => this.service.delete(locale, id).pipe(
      map(() => new DictionaryCourierDeleteSuccessAction({ id })),
      catchError((errors: HttpErrorResponse) => of(new DictionaryCourierDeleteFailAction({ id, errors })))
    ))
  );

  @Effect({ dispatch: false })
  crudFail$ = this.actions$.pipe(
    ofType(
      DictionaryCourierAddActionTypes.requestFail,
      DictionaryCourierEditActionTypes.requestFail
    ),
    map((action: DictionaryCourierAddFailAction | DictionaryCourierEditFailAction) => action.payload.errors),
    tap((data: HttpErrorResponse) => {
      if (data?.status === ResponseStatus.BAD_REQUEST || data?.status === ResponseStatus.UNPROCESSABLE_ENTITY) {
        this.snackBar.openFromComponent(LibSnackbarComponent, {
          data: {
            message: AlertUtil.getControlTranslation(data.error.detail),
            type: 'error'
          },
          duration: AppGlobal.defaultSnackBarDuration
        });
      }
    })
  );

  constructor(
    private actions$: Actions,
    private store: Store<ByKey>,
    private service: DictionaryCourierService,
    private snackBar: MatSnackBar
  ) {
  }
}
